import React, { useCallback, useState } from 'react';

import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import { Spacer } from '@repo/ui/components/Spacer';
import { H3 } from '@repo/ui/components/Typography';

import { formatMonetaryValue } from '../../../../utils/billing';
import {
  PaymentForm,
  PaymentFormProps,
  PaymentProvider,
} from '../../../Payment';
import PaySubscriptionButton from '../../ActionButtons/CreateSubscriptionActions/PaySubscriptionButton';
import useNextCharge from '../../hooks/useNextCharge';
import PaymentInfo from '../../PaymentInfo/PaymentInfo';
import {
  ActionButtonsWrapper,
  RightPanelBlock,
  RightPanelBottomBlock,
  RightPanelFormBlock,
  RightPanelWrapper,
} from '../../styles';
import usePaymentProviderData from '../hooks/usePaymentProviderData';
import usePlanPaymentDetails from '../hooks/usePlanPaymentDetails';

import { PlanPaymentDetailsProps } from './types';

/**
 * This component mounts the payment form and payment button.
 */
const PlanPaymentDetails: React.FC<PlanPaymentDetailsProps> = ({
  subscriptionChangePreviewData,
  onPaymentSuccess,
  returnUrlParams,
  layout,
}) => {
  // This state is used to disable the "Upgrade Now" button until the payment form is ready
  const [formComplete, setFormComplete] = useState(false);
  const [paymentFormLoaded, setPaymentFormLoaded] = useState(false);

  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );

  const paymentProviderData = usePaymentProviderData({
    currentSubscription,
  });

  const nextCharge = useNextCharge({
    currentSubscription,
  });

  const { options } = usePlanPaymentDetails(nextCharge);

  const handlePaymentFormReady: PaymentFormProps['onPaymentReady'] =
    useCallback((): void => {
      setPaymentFormLoaded(true);
    }, []);

  const handlePaymentForChange = useCallback((complete: boolean): void => {
    setFormComplete(complete);
  }, []);
  const paymentForm = (
    <PaymentForm
      layout={layout}
      options={options}
      onChange={handlePaymentForChange}
      onPaymentReady={handlePaymentFormReady}
    />
  );

  let buttonLabel: string | undefined;
  const formattedChargeAmount = nextCharge?.value
    ? formatMonetaryValue(nextCharge?.value)
    : undefined;
  if (subscriptionChangePreviewData.isPastDue && layout === 'accordion') {
    buttonLabel = `Pay Now - ${formattedChargeAmount}`;
  }

  const paymentButton = (
    <PaySubscriptionButton
      disabled={!formComplete || !paymentFormLoaded}
      label={buttonLabel}
      pastDue={subscriptionChangePreviewData.isPastDue}
      returnUrlParams={returnUrlParams}
      onSuccess={onPaymentSuccess}
    />
  );

  const accordionLayout = (
    <>
      <H3>Payment method</H3>
      <Spacer height="12px" />
      {paymentForm}
      <Spacer height="32px" />
      {paymentButton}
    </>
  );

  const columnLayout = (
    <RightPanelWrapper>
      <RightPanelBlock>
        <RightPanelFormBlock>
          <H3>Payment method</H3>
          <Spacer height="12px" />
          {paymentForm}
        </RightPanelFormBlock>
        <Spacer height="16px" />
        <RightPanelBottomBlock>
          <PaymentInfo
            currentSubscription={currentSubscription}
            subscriptionChangePreviewData={subscriptionChangePreviewData}
          />
        </RightPanelBottomBlock>
        <Spacer height="24px" />
      </RightPanelBlock>
      <ActionButtonsWrapper>{paymentButton}</ActionButtonsWrapper>
    </RightPanelWrapper>
  );

  return (
    <PaymentProvider {...paymentProviderData}>
      {layout === 'accordion' ? accordionLayout : columnLayout}
    </PaymentProvider>
  );
};

export default PlanPaymentDetails;
